import { useEffect, useRef, useState, useMemo } from "react";

export default function VaultAnimation() {
  const [tick, setTick] = useState(0);
  const reduced = useRef(typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  useEffect(() => {
    if (reduced.current) return;
    let raf;
    const loop = () => { setTick((t) => (t + 1) % 100000); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const tokens = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        id: i,
        offset: i * 0.32,
        lane: (i % 5) - 2,
        char: ["0x4F","0xA1","0xC2","0x91","0xFE","0x1B","0x77","0xD4","0x33","0xE8","0x07","0x52","0xBA","0x6C"][i],
      })),
    []
  );

  const t = tick / 60;

  return (
    <svg viewBox="0 0 600 480" preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="var(--rule-soft)" strokeWidth="0.5" />
        </pattern>
        <radialGradient id="vaultGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="var(--accent)" stopOpacity="0.18" />
          <stop offset="0.6" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="600" height="480" fill="url(#grid)" opacity="0.6" />

      <g stroke="var(--rule)" strokeWidth="0.5" fill="none">
        <line x1="0" y1="60" x2="40" y2="60" />
        <line x1="600" y1="60" x2="560" y2="60" />
        <line x1="0" y1="420" x2="40" y2="420" />
        <line x1="600" y1="420" x2="560" y2="420" />
        <line x1="60" y1="0" x2="60" y2="40" />
        <line x1="540" y1="0" x2="540" y2="40" />
        <line x1="60" y1="480" x2="60" y2="440" />
        <line x1="540" y1="480" x2="540" y2="440" />
      </g>

      <g fontFamily="var(--mono)" fontSize="9" fill="var(--fg-faint)">
        {tokens.map((tok) => {
          const phase = (t * 0.35 + tok.offset) % 1;
          const x = -20 + phase * 260;
          const y = 240 + tok.lane * 28 + Math.sin(t * 1.2 + tok.id) * 4;
          const enteringVault = phase > 0.85;
          const opacity =
            phase < 0.05 ? phase / 0.05 : phase > 0.85 ? Math.max(0, 1 - (phase - 0.85) / 0.15) : 1;
          return (
            <g key={tok.id} opacity={opacity}>
              <text x={x} y={y} fill={enteringVault ? "var(--accent)" : "var(--fg-faint)"}>{tok.char}</text>
              <line x1={x - 16} y1={y - 3} x2={x - 4} y2={y - 3}
                stroke={enteringVault ? "var(--accent)" : "var(--rule)"} strokeWidth="0.5" />
            </g>
          );
        })}
      </g>

      <g stroke="var(--rule)" strokeWidth="0.5" fill="none" opacity="0.7">
        {[-2, -1, 0, 1, 2].map((l) => (
          <line key={l} x1="120" y1={240 + l * 28} x2="280" y2="240" />
        ))}
      </g>

      <g transform="translate(420, 240)">
        <circle r="160" fill="url(#vaultGlow)" />
        <circle r="120" fill="none" stroke="var(--rule)" strokeWidth="1" />
        <circle r="120" fill="none" stroke="var(--accent)" strokeWidth="0.5"
          strokeDasharray="2 8" strokeDashoffset={t * 14} opacity="0.7" />
        <circle r="92" fill="none" stroke="var(--rule)" strokeWidth="0.5" />
        <g transform={`rotate(${t * -22})`}>
          <circle r="92" fill="none" stroke="var(--accent)" strokeWidth="1" strokeDasharray="40 220" opacity="0.9" />
        </g>
        <circle r="64" fill="var(--bg-1)" stroke="var(--rule)" strokeWidth="0.5" />
        <g transform={`rotate(${t * 36})`}>
          <circle r="64" fill="none" stroke="var(--accent)" strokeWidth="0.5" strokeDasharray="6 6" opacity="0.6" />
        </g>
        <g stroke="var(--fg)" strokeWidth="1" fill="none">
          <line x1="-128" y1="0" x2="-114" y2="0" />
          <line x1="114" y1="0" x2="128" y2="0" />
          <line x1="0" y1="-128" x2="0" y2="-114" />
          <line x1="0" y1="114" x2="0" y2="128" />
        </g>
        <g fontFamily="var(--mono)" fontSize="11" fill="var(--fg)" textAnchor="middle">
          <CipherGlyph t={t} />
        </g>
        <PulseRing t={t} />
        <g transform="translate(0, 88)">
          <text fontFamily="var(--mono)" fontSize="9" fill="var(--fg-faint)" textAnchor="middle" letterSpacing="2">
            SEALED · ON-DEVICE
          </text>
        </g>
      </g>

      <g transform="translate(560, 60)">
        <rect width="20" height="360" fill="none" stroke="var(--rule-soft)" />
        <rect width="20" height="3" y={(t * 60) % 357} fill="var(--accent)" opacity="0.6" />
        {Array.from({ length: 18 }).map((_, i) => (
          <line key={i} x1="0" y1={i * 20} x2="6" y2={i * 20} stroke="var(--rule)" strokeWidth="0.5" />
        ))}
      </g>
    </svg>
  );
}

function CipherGlyph({ t }) {
  const chars = "0123456789ABCDEF";
  const seed = Math.floor(t * 4);
  const out = [0, 1, 2, 3].map((i) => chars[(seed * 7 + i * 13) % 16]).join("");
  return (
    <g>
      <text y="6" letterSpacing="6">{out}</text>
      <text y="-30" fontSize="8" fill="var(--fg-faint)" letterSpacing="3">CIPHER</text>
    </g>
  );
}

function PulseRing({ t }) {
  const period = 1.4;
  const phase = (t % period) / period;
  const r = 64 + phase * 50;
  const opacity = (1 - phase) * 0.6;
  return <circle r={r} fill="none" stroke="var(--accent)" strokeWidth="1" opacity={opacity} />;
}
